import { useCallback, useEffect, useRef, useState } from 'react';
import { dropKind, droppedText } from './dropContent.js';
export function useDesktopDrop(options) {
    const [state, setState] = useState(null);
    const depth = useRef(0);
    const active = useRef(false);
    const cancelled = useRef(false);
    const reset = useCallback(() => { depth.current = 0; active.current = false; cancelled.current = false; setState(null); }, []);
    const blocked = (kind) => options.blockedReason
        ?? (document.querySelector('.gyro-file-dialog[open], .gyro-mode-popover') ? 'Close the open preview or menu to add content.' : undefined)
        ?? (kind === 'unsupported' ? 'Drop supported files, plain text, or links.' : kind === 'files' && options.filesFull ? 'Remove an attachment before adding another file.' : undefined);
    useEffect(() => { reset(); }, [options.enabled, options.scope, options.blockedReason, reset]);
    useEffect(() => {
        if (!options.enabled)
            return;
        const key = (event) => {
            if (!active.current)
                return;
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopImmediatePropagation();
                reset();
                cancelled.current = true;
            }
            else if (event.key === 'Shift' && !cancelled.current) {
                setState(previous => previous ? { ...previous, armed: (previous.kind === 'files' || event.type === 'keydown') && !previous.blockedReason } : previous);
            }
        };
        const outside = (event) => {
            // Protect the WebView from opening dropped files/URLs, even outside the panel's rounded edge.
            event.preventDefault();
            reset();
        };
        const outsideOver = (event) => { event.preventDefault(); if (event.dataTransfer)
            event.dataTransfer.dropEffect = 'none'; reset(); };
        window.addEventListener('keydown', key, true);
        window.addEventListener('keyup', key, true);
        window.addEventListener('blur', reset);
        window.addEventListener('dragend', reset);
        window.addEventListener('drop', outside);
        window.addEventListener('dragover', outsideOver);
        return () => {
            reset();
            window.removeEventListener('keydown', key, true);
            window.removeEventListener('keyup', key, true);
            window.removeEventListener('blur', reset);
            window.removeEventListener('dragend', reset);
            window.removeEventListener('drop', outside);
            window.removeEventListener('dragover', outsideOver);
        };
    }, [options.enabled, reset]);
    const track = (event) => {
        if (!options.enabled)
            return;
        event.preventDefault();
        event.stopPropagation();
        const kind = dropKind(Array.from(event.dataTransfer.types));
        const blockedReason = blocked(kind);
        // File attachments are an explicit drop gesture. Shift is only needed for selected text/links,
        // which can otherwise be dragged accidentally while editing the conversation.
        const armed = (kind === 'files' || event.shiftKey) && !blockedReason && !cancelled.current;
        event.dataTransfer.dropEffect = armed ? 'copy' : 'none';
        active.current = true;
        if (!cancelled.current) {
            const fileCount = kind === 'files' ? Array.from(event.dataTransfer.items).filter(item => item.kind === 'file').length || undefined : undefined;
            setState(previous => previous?.kind === kind && previous.armed === armed && previous.blockedReason === blockedReason && previous.fileCount === fileCount ? previous : { kind, armed, blockedReason, fileCount });
        }
    };
    const handlers = {
        onDragEnterCapture: (event) => {
            if (!options.enabled)
                return;
            if (depth.current === 0)
                cancelled.current = false;
            depth.current++;
            track(event);
        },
        onDragOverCapture: track,
        onDragLeaveCapture: (event) => {
            if (!options.enabled)
                return;
            event.preventDefault();
            event.stopPropagation();
            depth.current = Math.max(0, depth.current - 1);
            if (!depth.current)
                reset();
        },
        onDropCapture: (event) => {
            if (!options.enabled)
                return;
            event.preventDefault();
            event.stopPropagation();
            const kind = dropKind(Array.from(event.dataTransfer.types));
            const blockedReason = blocked(kind);
            const allowed = (kind === 'files' || event.shiftKey) && !blockedReason && !cancelled.current;
            reset();
            if (!allowed)
                return;
            try {
                if (kind === 'files') {
                    if (Array.from(event.dataTransfer.items).some(item => item.kind === 'file' && item.webkitGetAsEntry?.()?.isDirectory)) {
                        throw new Error('Drop individual files or a ZIP archive. Use Working folder to choose a folder.');
                    }
                    const files = Array.from(event.dataTransfer.files);
                    if (!files.length)
                        throw new Error('These files could not be read. Try the attachment button.');
                    options.onFiles(files);
                }
                else
                    options.onText(droppedText(kind, type => event.dataTransfer.getData(type)));
            }
            catch (error) {
                options.onError(error instanceof Error ? error.message : 'This content could not be added.');
            }
        },
    };
    return { state, handlers, reset };
}
//# sourceMappingURL=useDesktopDrop.js.map